import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import VideoCard from "@/components/base/VideoCard";
import Pagination from "@/components/base/Pagination";
import { sortOptions } from "@/mocks/categories";
import type { SortKey } from "@/lib/videos";

interface VideoResultListProps {
  videos: Video[];
  sort: SortKey;
  onSortChange: (value: SortKey) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  emptyTitle: string;
  emptyDescription: string;
  onReset: () => void;
  emptyActionTo?: string;
  emptyActionLabel?: string;
  resultCount?: number;
}

export default function VideoResultList({
  videos,
  sort,
  onSortChange,
  page,
  totalPages,
  onPageChange,
  emptyTitle,
  emptyDescription,
  onReset,
  emptyActionTo,
  emptyActionLabel,
  resultCount,
}: VideoResultListProps) {
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        {typeof resultCount === "number" && (
          <p className="text-xs text-foreground-500">
            共 <span className="text-accent-400">{resultCount}</span> 部作品 · 第 {page} / {totalPages} 页
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap sm:ml-auto">
          <span className="text-xs text-foreground-600 mr-1 whitespace-nowrap">排序</span>
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSortChange(option.value as SortKey)}
              className={`h-8 px-3 rounded-full text-xs transition-colors whitespace-nowrap cursor-pointer ${
                sort === option.value
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-400 hover:text-foreground-100 border border-background-200/70"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {videos.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300/70 bg-background-100 px-6 py-16 text-center">
          <span className="w-14 h-14 mx-auto rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
            <i className="ri-search-eye-line text-2xl" />
          </span>
          <h3 className="mt-4 text-base font-medium text-foreground-100">{emptyTitle}</h3>
          <p className="mt-2 text-xs text-foreground-500 max-w-md mx-auto leading-relaxed">{emptyDescription}</p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onReset}
              className="h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选条件
            </button>
            {emptyActionTo && (
              <Link
                to={emptyActionTo}
                className="h-9 px-4 rounded-md border border-background-300/70 text-xs text-foreground-200 hover:border-primary-400 transition-colors whitespace-nowrap flex items-center cursor-pointer"
              >
                {emptyActionLabel}
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {videos.map((video) => (
            <VideoCard key={video.id} video={video} showSummary />
          ))}
        </div>
      )}

      <Pagination page={page} totalPages={totalPages} onChange={onPageChange} />
    </div>
  );
}